import { ref, unref, computed } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store.js';

export function useBulkActions() {
  const store = useStore();
  const { t } = useI18n();

  const selectedConversations = useMapGetter(
    'bulkActions/getSelectedConversationIds'
  );
  const selectedInboxes = ref([]);
  const lastSelectedIndex = ref(null);

  const isProcessing = computed(
    () => store.getters['bulkActions/getUIFlags']?.isUpdating
  );

  const getConversationByInternalId = conversationId => {
    const getter = store.getters['conversations/getConversationById'];
    const id = Number(conversationId);
    return typeof getter === 'function' ? getter(id) : null;
  };

  const mapConversationIdsToDisplayIds = ids => {
    return ids
      .map(id => {
        const conversation = getConversationByInternalId(id);
        if (conversation?.display_id) {
          return conversation.display_id;
        }
        if (conversation?.id) {
          return conversation.id;
        }
        return id;
      })
      .filter(id => id !== undefined && id !== null);
  };

  function selectConversation(conversationId, inboxId, currentIndex = null) {
    store.dispatch('bulkActions/setSelectedConversationIds', Number(conversationId));
    selectedInboxes.value = [...selectedInboxes.value, inboxId];
    if (currentIndex !== null) {
      lastSelectedIndex.value = currentIndex;
    }
  }

  function deSelectConversation(conversationId, inboxId) {
    store.dispatch('bulkActions/removeSelectedConversationIds', Number(conversationId));
    selectedInboxes.value = selectedInboxes.value.filter(
      item => item !== inboxId
    );
  }

  function selectConversationRange(startIndex, endIndex, conversationList) {
    const availableConversations = unref(conversationList);
    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);
    
    const conversationsToSelect = availableConversations.slice(start, end + 1);
    const ids = conversationsToSelect
      .map(item => Number(item?.id))
      .filter(id => !Number.isNaN(id));
    
    store.dispatch('bulkActions/setSelectedConversationIds', ids);
    selectedInboxes.value = [
      ...selectedInboxes.value,
      ...conversationsToSelect.map(item => item.inbox_id)
    ];
  }

  function selectAllConversations(check, conversationList) {
    const availableConversations = unref(conversationList);
    if (check) {
      const ids = availableConversations
        .map(item => Number(item?.id))
        .filter(id => !Number.isNaN(id));
      store.dispatch('bulkActions/setSelectedConversationIds', ids);
      selectedInboxes.value = availableConversations.map(item => item.inbox_id);
      return;
    }
    resetBulkActions();
  }

  function resetBulkActions() {
    store.dispatch('bulkActions/clearSelectedConversationIds');
    selectedInboxes.value = [];
  }

  function isConversationSelected(id) {
    const normalizedId = Number(id);
    return selectedConversations.value.includes(normalizedId);
  }

  async function onDeleteConversations() {
    if (!selectedConversations.value.length) return false;

    const internalIds = [...selectedConversations.value];
    
    // Check if conversations are already trashed (permanent delete)
    const firstConversation = getConversationByInternalId(internalIds[0]);
    const isPermanentDelete = firstConversation?.status === 'trashed';
    
    const results = await Promise.allSettled(
      internalIds.map(conversationId =>
        store.dispatch('deleteConversation', Number(conversationId))
      )
    );

    const failed = results.filter(result => result.status === 'rejected');

    if (failed.length) {
      const errorKey = isPermanentDelete 
        ? 'BULK_ACTION.DELETE.PERMANENT_ERROR' 
        : 'BULK_ACTION.DELETE.ERROR';
      useAlert(t(errorKey));
      return false;
    }

    store.dispatch('bulkActions/clearSelectedConversationIds');
    selectedInboxes.value = [];
    
    const successKey = isPermanentDelete 
      ? 'BULK_ACTION.DELETE.PERMANENT_SUCCESS' 
      : 'BULK_ACTION.DELETE.SUCCESS';
    useAlert(t(successKey, { count: internalIds.length }));
    return true;
  }

  // Same method used in context menu, conversationId being passed from there.
  async function onAssignAgent(agent, conversationId = null) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: conversationId || selectedConversations.value,
        fields: {
          assignee_id: agent.id,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      if (conversationId) {
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL', {
            agentName: agent.name,
            conversationId,
          })
        );
      } else {
        useAlert(t('BULK_ACTION.ASSIGN_SUCCESFUL'));
      }
    } catch (err) {
      useAlert(t('BULK_ACTION.ASSIGN_FAILED'));
    }
  }

  // Same method used in context menu, conversationId being passed from there.
  async function onAssignLabels(newLabels, conversationId = null) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: conversationId || selectedConversations.value,
        labels: {
          add: newLabels,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      if (conversationId) {
        useAlert(
          t('CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL', {
            labelName: newLabels[0],
            conversationId,
          })
        );
      } else {
        useAlert(t('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL'));
      }
    } catch (err) {
      useAlert(t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
    }
  }

  async function onAssignTeamsForBulk(team) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: selectedConversations.value,
        fields: {
          team_id: team.id,
        },
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.TEAMS.ASSIGN_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.TEAMS.ASSIGN_FAILED'));
    }
  }

  async function onUpdateConversations(status, snoozedUntil) {
    try {
      await store.dispatch('bulkActions/process', {
        type: 'Conversation',
        ids: selectedConversations.value,
        fields: {
          status,
        },
        snoozed_until: snoozedUntil,
      });
      store.dispatch('bulkActions/clearSelectedConversationIds');
      useAlert(t('BULK_ACTION.UPDATE.UPDATE_SUCCESFUL'));
    } catch (err) {
      useAlert(t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
    }
  }

  return {
    selectedConversations,
    selectedInboxes,
    isProcessing,
    selectConversation,
    deSelectConversation,
    selectConversationRange,
    selectAllConversations,
    resetBulkActions,
    isConversationSelected,
    lastSelectedIndex,
    onAssignAgent,
    onAssignLabels,
    onAssignTeamsForBulk,
    onUpdateConversations,
    onDeleteConversations,
  };
}
